import { useEffect, useState } from "react";
import qrcode from "qrcode-generator";
import { confirmTOTPEnrollment, startTOTPEnrollment, type TOTPEnrollment } from "./api/client";

/**
 * QR code as an inline SVG. Rendering it here rather than linking an image
 * keeps the secret inside the page: an <img> pointed at a chart service would
 * hand somebody else's server the one value the second factor rests on.
 */
export function QRCode({ text, size = 200 }: { text: string; size?: number }) {
  const qr = qrcode(0, "M");
  qr.addData(text);
  qr.make();
  const count = qr.getModuleCount();
  const cells: string[] = [];
  for (let row = 0; row < count; row++) {
    for (let col = 0; col < count; col++) {
      if (qr.isDark(row, col)) cells.push(`M${col} ${row}h1v1h-1z`);
    }
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox={`-1 -1 ${count + 2} ${count + 2}`}
      role="img"
      aria-label="Authenticator setup code"
      style={{ background: "#fff", borderRadius: 6, padding: 4 }}
    >
      <path d={cells.join("")} fill="#000" />
    </svg>
  );
}

/** The secret in readable groups, for an authenticator that cannot scan. */
function grouped(secret: string): string {
  return (secret.match(/.{1,4}/g) ?? [secret]).join(" ");
}

/**
 * Enrol an authenticator: scan, prove a code, keep the recovery codes.
 *
 * The recovery codes are shown once and stored only as hashes, so the panel
 * refuses to close itself — the operator dismisses it, having had the chance to
 * write them down.
 */
export default function TwoFactorEnrollment({ onDone }: { onDone: (session?: { token: string }) => void }) {
  const [enrollment, setEnrollment] = useState<TOTPEnrollment | null>(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  // Returned when this enrolment was the thing standing between the caller and
  // a usable session.
  const [session, setSession] = useState<{ token: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    startTOTPEnrollment()
      .then((e) => live && setEnrollment(e))
      .catch((err: unknown) => live && setError(err instanceof Error ? err.message : String(err)));
    return () => {
      live = false;
    };
  }, []);

  async function confirm(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const out = await confirmTOTPEnrollment(code.trim());
      setCodes(out.codes);
      if (out.token) setSession({ token: out.token });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (codes) {
    return (
      <div>
        <h2 style={{ marginTop: 0 }}>Recovery codes</h2>
        <p className="muted" style={{ fontSize: 13 }}>
          Each of these works once, in place of a code from the authenticator. They are stored only as hashes, so this
          is the one time they can be shown. Keep them somewhere you can reach without your phone.
        </p>
        <div className="recovery-codes">
          {codes.map((c) => (
            <code key={c}>{c}</code>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button
            onClick={() => {
              void navigator.clipboard?.writeText(codes.join("\n"));
            }}
          >
            Copy
          </button>
          <button className="primary" onClick={() => onDone(session ?? undefined)}>
            I have saved them
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={confirm}>
      <h2 style={{ marginTop: 0 }}>Set up two-factor authentication</h2>
      {enrollment ? (
        <>
          <p className="muted" style={{ fontSize: 13 }}>
            Scan this with an authenticator app, then enter the code it shows.
          </p>
          <div style={{ display: "flex", justifyContent: "center", margin: "12px 0" }}>
            <QRCode text={enrollment.uri} />
          </div>
          <p className="muted" style={{ fontSize: 12, textAlign: "center" }}>
            Cannot scan? Enter this key by hand: <code>{grouped(enrollment.secret)}</code>
          </p>
          <label>Code from the app</label>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            autoFocus
          />
          <div style={{ marginTop: 14 }}>
            <button className="primary" type="submit" disabled={busy || code.length !== 6} style={{ width: "100%" }}>
              {busy ? "Checking…" : "Confirm"}
            </button>
          </div>
        </>
      ) : (
        <p className="muted">Preparing…</p>
      )}
      {error ? (
        <div className="banner error" style={{ marginTop: 14, marginBottom: 0 }}>
          {error}
        </div>
      ) : null}
    </form>
  );
}
